// Group waitlists.
//
// A waitlist entry is a member who wants into a group that has no room — or
// that they simply haven't been placed into yet. It is deliberately NOT a
// group membership: `groupId` and `group.playerIds` stay untouched, which is
// what lets someone be in one group on Monday and waiting for another on
// Wednesday at the same time.
//
// Stored as `group.waitlist: ["p001", "p014"]`, in the order people asked,
// and omitted entirely when empty so untouched groups stay byte-identical in
// kac_data.json.
import { isInstructionGroup } from "./tiers";

// Instruction groups take four to a court. Weekend Open Play and Private
// bookings have their own sizing, so they're treated as uncapped here rather
// than pretending the same number applies.
export const GROUP_CAPACITY = 4;

export function getWaitlist(group) {
  if (!Array.isArray(group?.waitlist)) return [];
  // Ids are de-duplicated on the way out as well as in, so a hand-edited
  // kac_data.json can't put someone in the queue twice.
  return [...new Set(group.waitlist.filter(id => typeof id === "string" && id))];
}

export function isWaiting(group, playerId) {
  return getWaitlist(group).includes(playerId);
}

// 1-based place in the queue, or 0 if they aren't on it.
export function waitlistPosition(group, playerId) {
  return getWaitlist(group).indexOf(playerId) + 1;
}

export function waitlistCount(group) {
  return getWaitlist(group).length;
}

// Spare seats, or null for groups that aren't capacity-capped.
export function spareSeats(group) {
  if (!group || !isInstructionGroup(group)) return null;
  return GROUP_CAPACITY - (group.playerIds?.length || 0);
}

export function hasSpace(group) {
  const spare = spareSeats(group);
  return spare === null ? true : spare > 0;
}

// Every group this member is waiting for, in schedule order rather than the
// order the groups happen to sit in the file.
export function groupsWaitingFor(groups, playerId) {
  return (Array.isArray(groups) ? groups : []).filter(g => isWaiting(g, playerId));
}

// The `waitlist` value a group should have once `playerId` is added. Someone
// already in the group is never queued for it — that's the one combination
// this feature must not create.
export function addToWaitlist(group, playerId) {
  if (!playerId) return getWaitlist(group);
  if ((group?.playerIds || []).includes(playerId)) return getWaitlist(group);
  const current = getWaitlist(group);
  if (current.includes(playerId)) return current;
  return [...current, playerId];
}

// Returns undefined when the queue empties, so updateGroup drops the field
// rather than leaving an empty array behind.
export function removeFromWaitlist(group, playerId) {
  const next = getWaitlist(group).filter(id => id !== playerId);
  return next.length > 0 ? next : undefined;
}

// Move someone one place up or down the queue. Order is the club's promise
// about who gets the next spot, so it has to be adjustable when a member's
// circumstances change.
export function moveInWaitlist(group, playerId, delta) {
  const list = getWaitlist(group);
  const from = list.indexOf(playerId);
  if (from === -1) return list;
  const to = from + delta;
  if (to < 0 || to >= list.length) return list;
  const next = [...list];
  next.splice(to, 0, next.splice(from, 1)[0]);
  return next;
}

// Groups that have both a free seat and somebody waiting — the backlog worth
// acting on. Used to decide whether to prompt after a member leaves a group.
export function groupsWithPlaceableWaiters(groups) {
  return (Array.isArray(groups) ? groups : [])
    .filter(g => waitlistCount(g) > 0 && hasSpace(g));
}
